import { useCallback, useEffect, useMemo, useRef } from 'react';
import { useParams, useSearchParams, Link } from 'react-router-dom';
import { ArrowLeft, Pencil, Search } from 'lucide-react';
import { useArticle, useSession } from '@/lib/queries';
import { useInPageFind } from '@/lib/use-in-page-find';
import { FindBar } from '@/components/find-bar';
import { HighlightedText } from '@/components/highlighted-text';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { formatDateIt } from '@/lib/utils';

export function ArticlePage() {
  const { id } = useParams();
  const article = useArticle(id);
  const { data: session } = useSession();
  const isAdmin = session?.user.role === 'admin';

  const [searchParams] = useSearchParams();
  const findParam = searchParams.get('find') ?? '';

  const a = article.data;

  // Segmenti in ordine di documento: [titolo, ...paragrafi]. Memoizzati così le
  // occorrenze si ricalcolano solo quando cambia l'articolo (non ad ogni render).
  const segments = useMemo<string[]>(() => {
    if (!a) return [];
    const paragraphs = a.contenuto
      .split(/\n{2,}/)
      .map((p) => p.trim())
      .filter(Boolean);
    const body = paragraphs.length > 1 ? paragraphs : [a.contenuto];
    return [a.titolo, ...body];
  }, [a]);

  const find = useInPageFind(segments, { initialQuery: findParam });
  const { setQuery, setOpen } = find;

  const currentMarkRef = useRef<HTMLElement | null>(null);
  const registerCurrent = useCallback((el: HTMLElement | null) => {
    currentMarkRef.current = el;
  }, []);

  // Porta in vista l'occorrenza corrente quando cambia.
  useEffect(() => {
    currentMarkRef.current?.scrollIntoView({ block: 'center', behavior: 'smooth' });
  }, [find.currentIndex, find.matches]);

  if (article.isLoading) {
    return <p className="container-narrow py-12 text-center text-sm text-(--color-muted-foreground)">Caricamento…</p>;
  }
  if (article.error || !a) {
    return (
      <div className="container-narrow py-12 text-center">
        <p className="text-sm text-(--color-destructive)">Articolo non trovato.</p>
        <Button asChild variant="link" className="mt-2">
          <Link to="/">Torna alla home</Link>
        </Button>
      </div>
    );
  }

  const body = segments.slice(1);
  const editQuery = find.query.trim();
  const editTo = editQuery
    ? `/admin/articoli/${a.id}?find=${encodeURIComponent(editQuery)}`
    : `/admin/articoli/${a.id}`;

  return (
    <article className="container-narrow py-8 sm:py-12">
      <div className="flex items-center justify-between gap-2 mb-6">
        <Button asChild variant="ghost" size="sm">
          <Link to={-1 as unknown as string}>
            <ArrowLeft className="h-4 w-4" /> Indietro
          </Link>
        </Button>
        <div className="flex items-center gap-2">
          <Button variant="ghost" size="sm" onClick={() => setOpen(true)}>
            <Search className="h-4 w-4" /> Cerca
          </Button>
          {isAdmin && (
            <Button asChild variant="outline" size="sm">
              <Link to={editTo}>
                <Pencil className="h-4 w-4" /> Modifica
              </Link>
            </Button>
          )}
        </div>
      </div>

      {find.open && (
        <div className="sticky top-16 z-30 mb-4 flex justify-end">
          <FindBar
            ref={find.inputRef}
            query={find.query}
            onQueryChange={setQuery}
            total={find.total}
            current={find.total === 0 ? 0 : find.currentIndex + 1}
            onPrev={find.prev}
            onNext={find.next}
            onClose={find.close}
            className="w-full max-w-sm"
          />
        </div>
      )}

      <header className="space-y-3 pb-6 border-b border-(--color-border)">
        <h1 className="font-serif text-2xl sm:text-3xl font-semibold leading-tight tracking-tight">
          <HighlightedText
            text={a.titolo}
            matches={find.matches.filter((m) => m.segmentIndex === 0)}
            currentIndex={find.currentIndex}
            registerCurrent={registerCurrent}
          />
        </h1>
        <div className="flex flex-wrap items-center gap-3 text-sm text-(--color-muted-foreground)">
          <time dateTime={a.data}>{formatDateIt(a.data)}</time>
          <Badge variant="outline">{a.fonte}</Badge>
        </div>
      </header>

      <div className="prose-article mt-6 w-full whitespace-break-spaces">
        {body.map((p, i) => (
          <p key={i}>
            <HighlightedText
              text={p}
              matches={find.matches.filter((m) => m.segmentIndex === i + 1)}
              currentIndex={find.currentIndex}
              registerCurrent={registerCurrent}
            />
          </p>
        ))}
      </div>
    </article>
  );
}
